import React from 'react';
import { range } from 'lodash-es';

import { Box, Divider, FormField, Input, Label, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../models/index';
import { AppliancesSection, Field } from '../../../models/index';
import type { SwitchObject } from '../../../models/Types';
import SwitchesWithTitle from '../../Widgets/SwitchesWithTitle';
import { APPLIANCES } from '../../../constants/index';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_APPLIANCES = 5;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: AppliancesSection) => void;
};

export default function Appliances({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: AppliancesSection,
  });

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(APPLIANCES.FIELD_NAME.NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function onRefrigeratorToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.REFRIGERATOR_IS_INCLUDED);
  }

  function onOvenRangeToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.OVEN_RANGE_IS_INCLUDED);
  }

  function onMicrowaveToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.MICROWAVE_IS_INCLUDED);
  }

  function onDishwasherToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.DISHWASHER_IS_INCLUDED);
  }

  function onClothesDryerToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.CLOTHES_DRYER_IS_INCLUDED);
  }

  function onClothesWasherToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.CLOTHES_WASHER_IS_INCLUDED);
  }

  function onWindowACToggle(): void {
    toggleSwitch(APPLIANCES.FIELD_NAME.WINDOW_AIR_CONDITIONER_IS_INCLUDED);
  }

  function toggleSwitch(key: string): void {
    setSectionValues({
      [key]: !section.getFieldValue(key),
    });
  }

  function renderAppliance(index: number): React.ReactNode {
    const nameField = section.getField(Field.getIndexedFieldName(APPLIANCES.FIELD_NAME.NAME, index));

    return (
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={
          <Box css={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'layout.tighter' }}>
            <Label id={`appliance-#${index + 1}`}>Additional item #{index + 1}</Label>
            <TextButton data-index={index} data-test="delete-appliance" onClick={onDeleteOne} textStyle="body-sm-bold">
              Delete
            </TextButton>
          </Box>
        }
        control={
          <Input
            data-index={index}
            disabled={section.isFieldDisabled(Field.getIndexedFieldName(APPLIANCES.FIELD_NAME.NAME, index))}
            data-test="name"
            value={nameField.enteredValue}
            onChange={setName}
          />
        }
      />
    );
  }

  const totalAppliances = section.getTotalIndexes();
  if (totalAppliances == null) {
    return null;
  }
  const appliances: Array<SwitchObject> = [
    {
      text: 'Refrigerator',
      onClick: onRefrigeratorToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.REFRIGERATOR_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.REFRIGERATOR_IS_INCLUDED),
    },
    {
      text: 'Oven/range',
      onClick: onOvenRangeToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.OVEN_RANGE_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.OVEN_RANGE_IS_INCLUDED),
    },
    {
      text: 'Microwave',
      onClick: onMicrowaveToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.MICROWAVE_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.MICROWAVE_IS_INCLUDED),
    },
    {
      text: 'Dishwasher',
      onClick: onDishwasherToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.DISHWASHER_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.DISHWASHER_IS_INCLUDED),
    },
    {
      text: 'Clothes washer',
      onClick: onClothesWasherToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.CLOTHES_WASHER_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.CLOTHES_WASHER_IS_INCLUDED),
    },
    {
      text: 'Clothes dryer',
      onClick: onClothesDryerToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.CLOTHES_DRYER_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.CLOTHES_DRYER_IS_INCLUDED),
    },
    {
      text: 'Window air conditioners',
      onClick: onWindowACToggle,
      toggled: section.getFieldValue(APPLIANCES.FIELD_NAME.WINDOW_AIR_CONDITIONER_IS_INCLUDED),
      disabled: section.isFieldDisabled(APPLIANCES.FIELD_NAME.WINDOW_AIR_CONDITIONER_IS_INCLUDED),
    },
  ];

  return (
    <>
      <SwitchesWithTitle
        switches={appliances}
        legend="What appliances, fixtures, and equipment are included with your rental property?"
      />
      {range(0, totalAppliances).map((index: number) => (
        <React.Fragment key={index}>
          {index >= 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
          {renderAppliance(index)}
        </React.Fragment>
      ))}
      {totalAppliances < MAXIMUM_APPLIANCES ? (
        totalAppliances >= 0 && (
          <TextButton onClick={onAddOne} icon={<IconPlusCircleFilled />} data-test="add-another">
            Add another appliance, fixture, or equipment
          </TextButton>
        )
      ) : (
        <Text data-test="maximum-reached">
          You’ve added {MAXIMUM_APPLIANCES} additional items, which is the maximum allowed.
        </Text>
      )}
    </>
  );
}
